/* eslint-disable consistent-return */
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import {
  getEmployeeSettingsWithServerSettings,
  getSystemSettings,
  updateEmployeeSettings,
  UpdateEmployeeSettings,
  updateSystemSettings,
  UpdateSystemSettings,
} from 'teto-client-api';
import MessageContext from './MessageContext';

export const geotaggingModeOptions = ['Require on All', 'Require on Punch Ins', 'Optional', 'Disabled'] as const;
export type GeotaggingMode = typeof geotaggingModeOptions[number];
export type DayOfWeekFormat = 0 | 1 | 2 | 3 | 4 | 5 | 6;
const dateFormatStrings = ['YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY'];
const timeFormatStrings = ['hh:mm A', 'HH:mm', 'HH:mm'];
const defaultDate = dayjs('2022-07-01 5:04:16 PM');

export const timeFormats = timeFormatStrings.map((i) => ({
  value: i,
  display: `${defaultDate.format(i)} (${i})`,
}));

export const dateFormats = dateFormatStrings.map((i) => ({
  value: i,
  display: `${defaultDate.format(i)} (${i})`,
}));

export interface SettingsContextState {
  settingsLoaded: boolean;
  settings: ServerSystemSettings &
  EmployeeSettings & { dateTimeFormat: string };
  // eslint-disable-next-line no-unused-vars
  refresh: () => Promise<void>;
  // eslint-disable-next-line no-unused-vars
  updateSystemSettings: (settings: UpdateSystemSettings) => Promise<void>;
  // eslint-disable-next-line no-unused-vars
  updateEmployeeSettings: (settings: UpdateEmployeeSettings) => Promise<void>;
}

export interface ServerSystemSettings {
  startDayOfWeek: DayOfWeekFormat;
  geotaggingMode: string;
  dateFormat: string;
  timeFormat: string;
  timeFormatPrecise: string;
  dateTimeFormat: string;
  payPeriodsCreateAutomatically: boolean;
  enableHourFactorInMyTimecards?: boolean;
  approverCanManageTime: boolean;
}

export interface EmployeeSettings {
  dateFormat: string;
  timeFormat: string;
  timeFormatPrecise: string;
  dateTimeFormat: string;
}

const SettingsContext = React.createContext<SettingsContextState>(
  null as never,
);

const SettingsContextProvider: React.FC<React.PropsWithChildren<{
  children: React.ReactNode;
}>> = (props) => {
  const { children } = props;
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // server settings
  const [startDateOfWeek, setStartDayOfWeek] = useState<DayOfWeekFormat>(0);
  const [dateTimeFormat, setDateTimeFormat] = useState<string>('');
  const [dateFormat, setDateFormat] = useState<string>('');
  const [timeFormat, setTimeFormat] = useState<string>('');
  const [timeFormatPrecise, setTimeFormatPrecise] = useState<string>('');
  const [geotaggingMode, setGeotaggingMode] = useState<GeotaggingMode>('Optional');
  const [payPeriodsCreateAutomatically, setPayPeriodsCreateAutomatically] = useState<boolean>(false);
  const [enableHourFactorInMyTimecards, setEnableHourFactorInMyTimecards] = useState(false);
  const [approverCanManageTime, setApproverCanManageTime] = useState<boolean>(false);

  const messageContext = useContext(MessageContext);

  const _refresh = () => getSystemSettings()
    .then((sysSettings) => {
      const sysSettingsUnprotected = (sysSettings as any);
      setEnableHourFactorInMyTimecards(!(sysSettingsUnprotected.TEHideHourFactorField === 1 || sysSettingsUnprotected.TEHideHourFactorField === true));
      getEmployeeSettingsWithServerSettings()
        .then((empSettings) => {
          setStartDayOfWeek(
            sysSettings.timeSheetStartDayOfWeek as DayOfWeekFormat,
          );
          setDateFormat(empSettings.webDateFormat);
          setTimeFormat(empSettings.webTimeFormat);
          setTimeFormatPrecise(empSettings.webTimeFormatPrecise);
          setDateTimeFormat(
            `${empSettings.webDateFormat} ${empSettings.webTimeFormat}`,
          );
          setGeotaggingMode(sysSettings.geotaggingMode as GeotaggingMode);
          setPayPeriodsCreateAutomatically(sysSettings.payPeriodsCreateAutomatically);
          setSettingsLoaded(true);
          setApproverCanManageTime(sysSettings.approversCanManageTime);
        })
        .catch((e) => {
          setSettingsLoaded(true);
          messageContext.setError(
            `Failed to load employee settings. ${e.message}`,
          );
        });
    })
    .catch((e) => {
      setSettingsLoaded(true);
      messageContext.setError(`Failed to load system settings. ${e.message}`);
    });

  const _updateSystemSettings = (settings: UpdateSystemSettings) => updateSystemSettings(settings).then(_refresh);
  const _updateEmployeeSettings = (settings: UpdateEmployeeSettings) => updateEmployeeSettings(settings).then(_refresh);

  useEffect(() => {
    _refresh();
  }, []);

  return (
    <SettingsContext.Provider
      value={{
        settingsLoaded,
        settings: {
          dateFormat,
          dateTimeFormat,
          startDayOfWeek: startDateOfWeek,
          timeFormat,
          timeFormatPrecise,
          geotaggingMode,
          payPeriodsCreateAutomatically,
          enableHourFactorInMyTimecards,
          approverCanManageTime,
        },
        updateEmployeeSettings: _updateEmployeeSettings,
        updateSystemSettings: _updateSystemSettings,
        refresh: _refresh,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;
export const SettingsProvider = SettingsContextProvider;
export const SettingsConsumer = SettingsContext;
